import { useMemo } from 'react'

import { useDispensaryPromos } from '../../../dispensaries'
import { CartDispensaryPromoCode } from '../../dispensary-promo'
import { concealCodeWithEncodedPromos, concealCodeWithoutEncodedPromos } from './concealCode'
import {
  isRestrictedFromInputForRootPromos,
  isRestrictedFromInputGlobal,
} from './isRestrictedFromInput'
import { translateCodeForDispensary, translateCodeForGlobal } from './translateCode'
import { UsePromoCode } from './types'
import { sanitizeInput } from './util'

export * from './types'

export interface UsePromoCodeUtilitiesProps {
  cartDispensaryPromoCodes: CartDispensaryPromoCode[] | undefined
}

export const usePromoCodeUtilities = ({
  cartDispensaryPromoCodes,
}: UsePromoCodeUtilitiesProps): UsePromoCode => {
  const { data: dispensaryPromosData, loading: dispensaryPromosLoading } = useDispensaryPromos()

  const concealCode = useMemo((): UsePromoCode['concealCode'] => {
    const dispensaryPromos = dispensaryPromosData?.getEncodedDispensaryPromos
    return !dispensaryPromos
      ? concealCodeWithoutEncodedPromos(cartDispensaryPromoCodes)
      : concealCodeWithEncodedPromos(
          dispensaryPromos.rootPromos,
          dispensaryPromos.promos,
          cartDispensaryPromoCodes,
        )
  }, [cartDispensaryPromoCodes, dispensaryPromosData?.getEncodedDispensaryPromos])

  const isCodeRestrictedFromUIInput = useMemo((): UsePromoCode['isCodeRestrictedFromUIInput'] => {
    const dispensaryPromos = dispensaryPromosData?.getEncodedDispensaryPromos
    return !dispensaryPromos
      ? isRestrictedFromInputGlobal
      : isRestrictedFromInputForRootPromos(dispensaryPromos.rootPromos)
  }, [dispensaryPromosData?.getEncodedDispensaryPromos])

  const translateCode = useMemo((): UsePromoCode['translateCode'] => {
    const dispensaryPromos = dispensaryPromosData?.getEncodedDispensaryPromos
    return !dispensaryPromos
      ? translateCodeForGlobal
      : translateCodeForDispensary(dispensaryPromos.promos)
  }, [dispensaryPromosData?.getEncodedDispensaryPromos])

  return {
    loading: dispensaryPromosLoading,
    concealCode,
    isCodeRestrictedFromUIInput,
    sanitizeInput,
    translateCode,
  }
}
